import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../api/ApiService";
import AppContext from "../../context/AppContext";

const HANDOFF_KEY = "icc_webview_handoff";
const DONE_KEY = "icc_webview_handoff_done";

// Friendly label for an account row in the picker.
function accountLabel(account) {
  return `${account.role || "Account"}${account.temple_name ? " - " + account.temple_name : ""}`;
}

export default function WebviewHandoff() {
  const { setToken, setUser } = useContext(AppContext);
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [handoffKey, setHandoffKey] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const doLogin = async (number, selectedAccountId, key) => {
    setBusy(true);
    try {
      const res = await ApiService.webviewLogin({ number, selectedAccountId }, key);
      localStorage.setItem("token", res.token);
      localStorage.setItem("user", JSON.stringify(res.user));
      setToken(res.token);
      setUser(res.user);
      setShow(false);
      navigate("/");
    } catch (e) {
      // Handoff failed — fall back silently to the normal login page.
      setBusy(false);
      setShow(false);
    }
  };

  useEffect(() => {
    const raw = localStorage.getItem(HANDOFF_KEY);
    if (!raw) return;
    // Consume once, before any network call, so a mismatch/failure can't loop.
    localStorage.removeItem(HANDOFF_KEY);

    let number = raw;
    let key = "";
    let sid = "";
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        if (parsed.phone) number = parsed.phone;
        if (parsed.key) key = parsed.key;
        if (parsed.sid) sid = parsed.sid;
      }
    } catch (e) {
      // raw is the bare phone string.
    }

    // Same WebView session already handled → the user logged out on purpose
    // (or already went through the popup). Don't log them back in.
    if (sid && localStorage.getItem(DONE_KEY) === sid) return;
    if (sid) localStorage.setItem(DONE_KEY, sid);

    // Already logged in? Ignore the handoff.
    if (localStorage.getItem("token")) return;
    if (!number) return;
    setPhone(number);
    setHandoffKey(key);

    (async () => {
      try {
        const res = await ApiService.getWebviewAccounts(number, key);
        const list = res?.accounts || [];
        if (!list.length) return; // no account here → normal login page
        if (list.length === 1) {
          await doLogin(number, list[0].id, key);
        } else {
          setAccounts(list);
          setShow(true);
        }
      } catch (e) {
        // No account / error → normal login page.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!show) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h4 style={styles.title}>Choose your account</h4>
        <p style={styles.subtitle}>Multiple accounts are registered on +91 {phone}. Select one to continue.</p>
        <select
          style={styles.select}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={busy}
        >
          <option value="">Choose an account</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {accountLabel(account)}
            </option>
          ))}
        </select>
        <button
          style={{ ...styles.button, opacity: !selected || busy ? 0.6 : 1 }}
          disabled={!selected || busy}
          onClick={() => doLogin(phone, selected, handoffKey)}
        >
          {busy ? "Signing in…" : "Continue"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 380,
    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
  },
  title: { margin: "0 0 8px", color: "#9F1D21", fontWeight: 700 },
  subtitle: { margin: "0 0 16px", fontSize: 14, color: "#555" },
  select: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", marginBottom: 16 },
  button: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "none",
    background: "#9F1D21",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
};
